/**
 * Tiny History-API router. Routes are `{ path: '/cards/:slug', load: () => import(...) }`;
 * a page module exports `render(el, params)` and may return a cleanup function (used to
 * dispose Three.js scenes when navigating away).
 *
 * @typedef {{ path: string, load: () => Promise<{ render: (el: HTMLElement, params: Record<string, string>) => (void | (() => void) | Promise<void | (() => void)>) }> }} Route
 */

/** @param {string} path */
const compile = (path) =>
  new RegExp(`^${path.replace(/\/:([a-zA-Z_]+)/g, '/(?<$1>[^/]+)').replace(/\/$/, '')}/?$`);

/**
 * @param {HTMLElement} outlet
 * @param {Route[]} routes
 * @param {Route} notFound
 */
export function createRouter(outlet, routes, notFound) {
  const compiled = routes.map((r) => ({ ...r, re: compile(r.path) }));
  /** @type {void | (() => void)} */
  let cleanup;
  let navId = 0;

  async function resolve() {
    const id = ++navId;
    const { pathname } = window.location;
    let params = {};
    const match = compiled.find((r) => {
      const m = pathname.match(r.re);
      if (m) params = { ...m.groups };
      return Boolean(m);
    });
    let mod;
    try {
      mod = await (match ?? notFound).load();
    } catch (err) {
      // A deploy replaced the hashed chunks this tab knew about: reload once to pick up the
      // new build instead of leaving a blank page.
      const key = `fp_chunk_reload:${pathname}`;
      let retry = false;
      try {
        // At most one reload per path every 30 s, so a real outage can't loop.
        retry = Date.now() - Number(sessionStorage.getItem(key) ?? 0) > 30000;
        sessionStorage.setItem(key, String(Date.now()));
      } catch {
        /* storage blocked */
      }
      if (retry) {
        window.location.reload();
        return;
      }
      throw err;
    }
    try {
      sessionStorage.removeItem(`fp_chunk_reload:${pathname}`);
    } catch {
      /* storage blocked */
    }
    if (id !== navId) return; // a newer navigation won
    if (typeof cleanup === 'function') cleanup();
    outlet.replaceChildren();
    cleanup = await mod.render(outlet, params);
  }

  /** @param {string} to */
  function navigate(to, { replace = false } = {}) {
    if (to === window.location.pathname + window.location.search) return;
    history[replace ? 'replaceState' : 'pushState']({}, '', to);
    resolve();
  }

  document.addEventListener('click', (e) => {
    const a = /** @type {HTMLElement} */ (e.target).closest?.('a[href]');
    if (!a || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey) return;
    const url = new URL(/** @type {HTMLAnchorElement} */ (a).href);
    if (url.origin !== window.location.origin || a.hasAttribute('download') || a.target) return;
    e.preventDefault();
    navigate(url.pathname + url.search);
  });
  window.addEventListener('popstate', resolve);

  return { navigate, start: resolve };
}
